package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.web.hooks.CanvasGestureConfig
import com.fantasytenis.web.hooks.CanvasViewport
import com.fantasytenis.web.hooks.useCanvasGestures
import com.fantasytenis.web.theme.Colors
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Layout configuration for infinite canvas bracket.
 */
object CanvasLayoutConfig {
    // Match dimensions - sized for two player rows with comfortable padding
    const val MATCH_WIDTH = 200
    const val MATCH_HEIGHT = 88      // Full variant with header
    const val MATCH_HEIGHT_COMPACT = 84  // Compact variant without header - needs room for 2 player rows with potential players subtitle

    // Spacing - wider for visible connector lines
    const val ROUND_GAP = 160
    const val MATCH_VERTICAL_GAP = 16

    // Champion/Final box
    const val CHAMPION_WIDTH = 180
    const val CHAMPION_HEIGHT = 100

    // Center gap - space between the two bracket halves where the Final match sits
    // Must be wide enough for CHAMPION_WIDTH plus margins on both sides
    const val CENTER_GAP = 260  // 180 (champion width) + 40 margin on each side

    // Canvas padding
    const val CONTAINER_PADDING = 24

    // Zoom constraints
    const val MIN_ZOOM = 0.15
    const val MAX_ZOOM = 2.0

    // Default zoom levels by draw size - adjusted for larger layout
    fun getDefaultZoom(drawSize: Int): Double = when {
        drawSize >= 128 -> 0.25
        drawSize >= 64 -> 0.40
        drawSize >= 32 -> 0.65
        else -> 0.90
    }
}

/**
 * Infinite canvas bracket view with pan/zoom capabilities.
 *
 * Displays the full bracket on a pannable, zoomable canvas surface.
 * Users can:
 * - Pan by click-dragging (or touch-dragging on mobile)
 * - Zoom via mouse wheel, trackpad pinch, or touch pinch
 * - Use keyboard shortcuts (arrows for pan, +/- for zoom, 0 for fit)
 * - Click on players to make picks
 */
@Composable
fun InfiniteCanvasBracket(
    rounds: List<BracketRoundEntity>,
    drawSize: Int,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit,
    /**
     * All rounds including early rounds for pick advancement calculation.
     * When displaying filtered rounds, pass the full rounds here
     * so PickAdvancementCalculator can trace feeder matches correctly.
     */
    allRoundsForPickCalculation: List<BracketRoundEntity>? = null
) {
    // Calculate layout using canvas-optimized config
    val layout = remember(rounds, drawSize) {
        CanvasBracketLayoutCalculator.calculateLayout(rounds, drawSize)
    }

    // Track viewport dimensions
    var viewportWidth by remember { mutableStateOf(0.0) }
    var viewportHeight by remember { mutableStateOf(0.0) }

    // Viewport state for pan/zoom
    var viewport by remember(drawSize, layout) {
        mutableStateOf(
            CanvasViewport(
                panX = 0.0,
                panY = 0.0,
                zoom = CanvasLayoutConfig.getDefaultZoom(drawSize),
                worldWidth = layout.totalWidth,
                worldHeight = layout.totalHeight
            )
        )
    }

    // Fit to screen on initial render and when layout changes
    LaunchedEffect(viewportWidth, viewportHeight, layout) {
        if (viewportWidth > 0 && viewportHeight > 0) {
            viewport = viewport.copy(
                worldWidth = layout.totalWidth,
                worldHeight = layout.totalHeight
            ).fitToScreen(viewportWidth, viewportHeight)
        }
    }

    // Gesture handling
    val gestureConfig = remember {
        CanvasGestureConfig(
            minZoom = CanvasLayoutConfig.MIN_ZOOM,
            maxZoom = CanvasLayoutConfig.MAX_ZOOM
        )
    }

    val gestureHandlers = useCanvasGestures(
        viewport = viewport,
        onViewportChange = { newViewport -> viewport = newViewport },
        viewportWidth = viewportWidth,
        viewportHeight = viewportHeight,
        config = gestureConfig
    )

    // Create match lookup map
    val matchMap = remember(rounds) {
        rounds.flatMap { it.matches }.associateBy { it.id }
    }

    // Track hovered match for visual feedback
    var hoveredMatchId by remember { mutableStateOf<String?>(null) }

    // Main container
    Div({
        classes("infinite-canvas-container")
        style {
            width(100.percent)
            height(100.percent)
            position(Position.Relative)
            overflow("hidden")
            backgroundColor(Colors.Background)
        }
    }) {
        // Canvas viewport (captures all interactions)
        Div({
            classes("infinite-canvas-viewport")
            style {
                width(100.percent)
                height(100.percent)
                overflow("hidden")
                cursor(if (gestureHandlers.isPanning) "grabbing" else "grab")
                property("touch-action", "none")
                property("user-select", "none")
            }
            // Capture viewport dimensions
            ref { element ->
                val updateSize = {
                    viewportWidth = element.clientWidth.toDouble()
                    viewportHeight = element.clientHeight.toDouble()
                }
                updateSize()
                val resizeHandler: (dynamic) -> Unit = { updateSize() }
                window.addEventListener("resize", resizeHandler)
                onDispose {
                    window.removeEventListener("resize", resizeHandler)
                }
            }
            // Attach gesture handlers via native events
            addEventListener("wheel") { gestureHandlers.onWheel(it.nativeEvent) }
            addEventListener("mousedown") { gestureHandlers.onMouseDown(it.nativeEvent) }
            addEventListener("mousemove") { gestureHandlers.onMouseMove(it.nativeEvent) }
            addEventListener("mouseup") { gestureHandlers.onMouseUp(it.nativeEvent) }
            addEventListener("mouseleave") { gestureHandlers.onMouseLeave(it.nativeEvent) }
            addEventListener("touchstart") { gestureHandlers.onTouchStart(it.nativeEvent) }
            addEventListener("touchmove") { gestureHandlers.onTouchMove(it.nativeEvent) }
            addEventListener("touchend") { gestureHandlers.onTouchEnd(it.nativeEvent) }
        }) {
            // Canvas world (transformed content)
            Div({
                classes("infinite-canvas-world")
                style {
                    property("position", "absolute")
                    property("width", "${layout.totalWidth.toInt()}px")
                    property("height", "${layout.totalHeight.toInt()}px")
                    property("transform", "translate(${viewport.panX}px, ${viewport.panY}px) scale(${viewport.zoom})")
                    property("transform-origin", "0 0")
                    property("will-change", "transform")
                }
            }) {
                // SVG layer for connector lines
                BracketConnectorSvg(
                    connectors = layout.connectors,
                    totalWidth = layout.totalWidth,
                    totalHeight = layout.totalHeight,
                    highlightedMatchId = hoveredMatchId
                )

                // Match nodes
                // Determine the first round for this draw size to optimize player display
                val firstRound = RoundName.getRoundsForDrawSize(drawSize).first()
                val roundsForPicks = allRoundsForPickCalculation ?: rounds

                layout.positions.forEach { position ->
                    val match = matchMap[position.matchId]
                    if (match != null) {
                        // Compute display players based on round type
                        // For first-round matches, use match data directly (like GuidedView)
                        // For later rounds, use calculator to trace pick advancement
                        val (p1Display, p2Display) = if (match.round == firstRound) {
                            // First round: use match data directly - this works like GuidedView
                            Pair(
                                DisplayPlayer(
                                    name = match.player1.displayName,
                                    seed = match.player1.seed,
                                    country = match.player1.playerCountry,
                                    playerId = match.player1.playerId,
                                    isFromPick = false
                                ),
                                DisplayPlayer(
                                    name = match.player2.displayName,
                                    seed = match.player2.seed,
                                    country = match.player2.playerCountry,
                                    playerId = match.player2.playerId,
                                    isFromPick = false
                                )
                            )
                        } else {
                            // Later rounds: use calculator to trace pick advancement
                            PickAdvancementCalculator.getMatchDisplayPlayers(
                                match = match,
                                picks = picks,
                                allRounds = roundsForPicks
                            )
                        }

                        Div({
                            style {
                                property("position", "absolute")
                                property("left", "${position.x.toInt()}px")
                                property("top", "${position.y.toInt()}px")
                                property("z-index", "1")
                            }
                            onMouseEnter { hoveredMatchId = position.matchId }
                            onMouseLeave { hoveredMatchId = null }
                        }) {
                            BracketMatchNode(
                                match = match,
                                selectedWinnerId = picks[match.player1.id],
                                isViewOnly = isViewOnly,
                                variant = getCanvasVariant(drawSize, position.round),
                                width = position.width,
                                height = position.height,
                                onSelectWinner = { winnerId ->
                                    onSelectWinner(match.player1.id, winnerId)
                                },
                                onShowDetails = { onShowMatchDetails(match) },
                                player1Display = PlayerDisplayData(
                                    name = p1Display.name,
                                    seed = p1Display.seed,
                                    country = p1Display.country,
                                    playerId = p1Display.playerId,
                                    isFromPick = p1Display.isFromPick,
                                    potentialPlayers = p1Display.potentialPlayers
                                ),
                                player2Display = PlayerDisplayData(
                                    name = p2Display.name,
                                    seed = p2Display.seed,
                                    country = p2Display.country,
                                    playerId = p2Display.playerId,
                                    isFromPick = p2Display.isFromPick,
                                    potentialPlayers = p2Display.potentialPlayers
                                )
                            )
                        }
                    }
                }

                // Champion display
                val finalMatch = rounds.find { it.name == RoundName.F }?.matches?.firstOrNull()
                if (finalMatch != null) {
                    val championId = picks[finalMatch.player1.id]
                    val champion = when (championId) {
                        finalMatch.player1.playerId -> finalMatch.player1
                        finalMatch.player2.playerId -> finalMatch.player2
                        else -> null
                    }

                    if (champion != null) {
                        Div({
                            style {
                                property("position", "absolute")
                                property("left", "${layout.championX.toInt()}px")
                                property("top", "${(layout.championY - 70).toInt()}px")
                                property("z-index", "2")
                            }
                        }) {
                            CanvasChampionDisplay(
                                playerName = champion.displayName,
                                seed = champion.seed
                            )
                        }
                    }
                }
            }
        }

        // Floating controls overlay
        CanvasControls(
            zoom = viewport.zoom,
            onZoomIn = gestureHandlers.zoomIn,
            onZoomOut = gestureHandlers.zoomOut,
            onFitToScreen = gestureHandlers.fitToScreen
        )
    }
}

/**
 * Determine match node variant for canvas display.
 * Always use COMPACT to ensure full player names are visible.
 */
private fun getCanvasVariant(@Suppress("UNUSED_PARAMETER") drawSize: Int, round: RoundName): MatchNodeVariant {
    // Note: drawSize parameter kept for API consistency, but not used since we always want full names
    return when (round) {
        RoundName.F -> MatchNodeVariant.FULL
        RoundName.SF -> MatchNodeVariant.COMPACT
        else -> MatchNodeVariant.COMPACT  // Always use COMPACT to show full names (MINI truncates to 6 chars)
    }
}

/**
 * Champion display optimized for canvas.
 */
@Composable
private fun CanvasChampionDisplay(
    playerName: String,
    seed: Int?
) {
    Div({
        style {
            backgroundColor(Colors.Secondary)
            borderRadius(10.px)
            padding(12.px, 16.px)
            textAlign("center")
            property("box-shadow", "0 4px 12px rgba(255, 215, 0, 0.4)")
            minWidth(140.px)
        }
    }) {
        // Trophy
        Div({
            style {
                fontSize(24.px)
                marginBottom(4.px)
            }
        }) {
            Text("\uD83C\uDFC6")
        }

        // Champion label
        Div({
            style {
                fontSize(10.px)
                fontWeight(600)
                color(Colors.TextSecondary)
                marginBottom(2.px)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text("Champion")
        }

        // Name
        Div({
            style {
                fontSize(13.px)
                fontWeight(700)
                color(Colors.TextPrimary)
            }
        }) {
            seed?.let {
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(9.px)
                        fontWeight(600)
                        padding(1.px, 4.px)
                        borderRadius(3.px)
                        marginRight(6.px)
                    }
                }) {
                    Text("$it")
                }
            }
            Text(playerName)
        }
    }
}

/**
 * Layout calculator optimized for canvas display.
 * Uses smaller dimensions for better density at low zoom levels.
 */
object CanvasBracketLayoutCalculator {

    fun calculateLayout(
        rounds: List<BracketRoundEntity>,
        drawSize: Int
    ): BracketLayout {
        val matchHeight = if (drawSize >= 64) CanvasLayoutConfig.MATCH_HEIGHT_COMPACT else CanvasLayoutConfig.MATCH_HEIGHT
        val positions = mutableListOf<MatchPosition>()
        val connectors = mutableListOf<ConnectorLine>()

        val roundNames = RoundName.getRoundsForDrawSize(drawSize)
        val numRounds = roundNames.size
        val halfWidth = (numRounds - 1) * CanvasLayoutConfig.ROUND_GAP + CanvasLayoutConfig.MATCH_WIDTH

        // Calculate total height based on first round
        // Each first-round match gets 2.5x its height in vertical space for good visual separation
        val firstRoundMatches = drawSize / 2
        val firstRoundMatchesInHalf = firstRoundMatches / 2  // Used for all vertical positioning
        val baseSpacingPerMatch = (matchHeight * 2.5).toInt()  // 2.5x height = match + 1.5x whitespace
        val firstRoundHeight = firstRoundMatchesInHalf * baseSpacingPerMatch
        val totalHeight = (firstRoundHeight + CanvasLayoutConfig.CONTAINER_PADDING * 2).toDouble()

        // Total width calculation - use CENTER_GAP for the space between bracket halves
        val totalWidth = (halfWidth * 2 + CanvasLayoutConfig.CENTER_GAP + CanvasLayoutConfig.CONTAINER_PADDING * 2).toDouble()

        // Champion/Final X position - centered in the gap between left and right bracket halves
        // Left half ends at: PADDING + halfWidth
        // Right half starts at: PADDING + halfWidth + CENTER_GAP
        // Center of gap is: PADDING + halfWidth + CENTER_GAP/2
        val leftHalfEnd = CanvasLayoutConfig.CONTAINER_PADDING + halfWidth
        val rightHalfStart = leftHalfEnd + CanvasLayoutConfig.CENTER_GAP
        val gapCenter = (leftHalfEnd + rightHalfStart) / 2.0
        val championX = gapCenter - CanvasLayoutConfig.CHAMPION_WIDTH / 2.0

        // Champion/Final Y position - will be calculated after we know where the semifinals are
        // For now, use a placeholder that will be updated
        var championY = totalHeight / 2.0 - CanvasLayoutConfig.CHAMPION_HEIGHT / 2.0

        // Process each round
        for ((roundIndex, roundName) in roundNames.withIndex()) {
            val roundData = rounds.find { it.name == roundName } ?: continue
            val matchesInRound = roundData.matches.size

            if (roundName != RoundName.F) {
                val matchesPerHalfInRound = matchesInRound / 2

                // Left half
                for (i in 0 until matchesPerHalfInRound) {
                    val match = roundData.matches[i]
                    val position = calculateMatchPosition(
                        match = match,
                        roundIndex = roundIndex,
                        matchIndexInHalf = i,
                        firstRoundMatchesInHalf = firstRoundMatchesInHalf,
                        totalHeight = totalHeight,
                        matchHeight = matchHeight,
                        isLeftHalf = true,
                        numRounds = numRounds
                    )
                    positions.add(position)
                }

                // Right half
                for (i in 0 until matchesPerHalfInRound) {
                    val match = roundData.matches[matchesPerHalfInRound + i]
                    val position = calculateMatchPosition(
                        match = match,
                        roundIndex = roundIndex,
                        matchIndexInHalf = i,
                        firstRoundMatchesInHalf = firstRoundMatchesInHalf,
                        totalHeight = totalHeight,
                        matchHeight = matchHeight,
                        isLeftHalf = false,
                        numRounds = numRounds
                    )
                    positions.add(position)
                }
            } else {
                // Final match - position based on actual semifinal positions
                val match = roundData.matches.firstOrNull()
                if (match != null) {
                    val sfPositions = positions.filter { it.round == RoundName.SF }
                    val leftSF = sfPositions.find { it.isLeftHalf }
                    val rightSF = sfPositions.find { !it.isLeftHalf }

                    // Calculate X: center between left SF's right edge and right SF's left edge
                    val finalX = if (leftSF != null && rightSF != null) {
                        val leftSfRightEdge = leftSF.x + leftSF.width
                        val rightSfLeftEdge = rightSF.x
                        val gapCenter = (leftSfRightEdge + rightSfLeftEdge) / 2.0
                        gapCenter - CanvasLayoutConfig.CHAMPION_WIDTH / 2.0
                    } else {
                        championX  // fallback to calculated value
                    }

                    // Calculate Y: align vertical center with the SF's vertical center
                    val finalY = if (leftSF != null) {
                        val sfCenterY = leftSF.y + leftSF.height / 2.0
                        sfCenterY - CanvasLayoutConfig.CHAMPION_HEIGHT / 2.0
                    } else {
                        championY  // fallback
                    }

                    positions.add(
                        MatchPosition(
                            matchId = match.id,
                            round = roundName,
                            matchNumber = match.matchNumber,
                            x = finalX,
                            y = finalY,
                            width = CanvasLayoutConfig.CHAMPION_WIDTH,
                            height = CanvasLayoutConfig.CHAMPION_HEIGHT,
                            isLeftHalf = true
                        )
                    )
                }
            }
        }

        // Calculate connectors
        connectors.addAll(calculateConnectors(positions, roundNames))

        return BracketLayout(
            positions = positions,
            connectors = connectors,
            totalWidth = totalWidth,
            totalHeight = totalHeight,
            championX = championX,
            championY = championY
        )
    }

    private fun calculateMatchPosition(
        match: BracketMatchEntity,
        roundIndex: Int,
        matchIndexInHalf: Int,
        firstRoundMatchesInHalf: Int,
        totalHeight: Double,
        matchHeight: Int,
        isLeftHalf: Boolean,
        numRounds: Int
    ): MatchPosition {
        // Calculate X position
        // Left half: starts at PADDING, each round moves right by ROUND_GAP
        // Right half: starts at right edge (PADDING + 2*halfWidth + CENTER_GAP), each round moves left by ROUND_GAP
        val halfWidth = (numRounds - 1) * CanvasLayoutConfig.ROUND_GAP + CanvasLayoutConfig.MATCH_WIDTH
        val x = if (isLeftHalf) {
            CanvasLayoutConfig.CONTAINER_PADDING + roundIndex * CanvasLayoutConfig.ROUND_GAP.toDouble()
        } else {
            // Right half starts after left half + center gap
            val rightHalfStart = CanvasLayoutConfig.CONTAINER_PADDING + halfWidth + CanvasLayoutConfig.CENTER_GAP
            // First round of right half is at the far right, moves left as rounds progress
            rightHalfStart + (numRounds - 2 - roundIndex) * CanvasLayoutConfig.ROUND_GAP.toDouble()
        }

        val y = calculateVerticalPosition(
            roundIndex = roundIndex,
            matchIndexInHalf = matchIndexInHalf,
            firstRoundMatchesInHalf = firstRoundMatchesInHalf,
            totalHeight = totalHeight,
            matchHeight = matchHeight
        )

        return MatchPosition(
            matchId = match.id,
            round = match.round,
            matchNumber = match.matchNumber,
            x = x,
            y = y,
            width = CanvasLayoutConfig.MATCH_WIDTH,
            height = matchHeight,
            isLeftHalf = isLeftHalf
        )
    }

    /**
     * Calculate the vertical (Y) position for a match.
     *
     * Uses the first round's match count as baseline, then spreads later rounds
     * exponentially so each match is centered between its two feeder matches.
     */
    private fun calculateVerticalPosition(
        roundIndex: Int,
        matchIndexInHalf: Int,
        firstRoundMatchesInHalf: Int,
        totalHeight: Double,
        matchHeight: Int
    ): Double {
        val usableHeight = totalHeight - CanvasLayoutConfig.CONTAINER_PADDING * 2
        // Always base spacing on first round's match count
        val baseSpacing = usableHeight / firstRoundMatchesInHalf
        // Each subsequent round spreads matches 2x further apart
        val spreadFactor = 1 shl roundIndex  // 1, 2, 4, 8... for rounds 0, 1, 2, 3...
        val adjustedSpacing = baseSpacing * spreadFactor

        return CanvasLayoutConfig.CONTAINER_PADDING +
            matchIndexInHalf * adjustedSpacing +
            adjustedSpacing / 2 - matchHeight / 2
    }

    private fun calculateConnectors(
        positions: List<MatchPosition>,
        roundNames: List<RoundName>
    ): List<ConnectorLine> {
        val connectors = mutableListOf<ConnectorLine>()
        val positionsByRound = positions.groupBy { it.round }

        for (i in 0 until roundNames.size - 1) {
            val currentRound = roundNames[i]
            val nextRound = roundNames[i + 1]

            val currentPositions = positionsByRound[currentRound] ?: continue
            val nextPositions = positionsByRound[nextRound] ?: continue

            // Special case: Semifinals to Final
            // The Final receives connectors from BOTH the left and right semifinals
            if (nextRound == RoundName.F) {
                val finalMatch = nextPositions.firstOrNull() ?: continue
                val leftSF = currentPositions.filter { it.isLeftHalf }.sortedBy { it.y }
                val rightSF = currentPositions.filter { !it.isLeftHalf }.sortedBy { it.y }

                // Connect left semifinal to Final (connector goes right)
                leftSF.forEach { sf ->
                    connectors.add(createConnectorToFinal(sf, finalMatch, isFromLeftHalf = true))
                }

                // Connect right semifinal to Final (connector goes left)
                rightSF.forEach { sf ->
                    connectors.add(createConnectorToFinal(sf, finalMatch, isFromLeftHalf = false))
                }
                continue
            }

            // Normal case: separate by half BEFORE chunking to avoid mixing left/right halves
            val currentLeft = currentPositions.filter { it.isLeftHalf }.sortedBy { it.y }
            val currentRight = currentPositions.filter { !it.isLeftHalf }.sortedBy { it.y }
            val nextLeft = nextPositions.filter { it.isLeftHalf }.sortedBy { it.y }
            val nextRight = nextPositions.filter { !it.isLeftHalf }.sortedBy { it.y }

            // Process left half
            currentLeft.chunked(2).forEachIndexed { index, pair ->
                if (pair.size == 2 && index < nextLeft.size) {
                    val nextMatch = nextLeft[index]
                    pair.forEach { current ->
                        connectors.add(createConnector(current, nextMatch))
                    }
                }
            }

            // Process right half
            currentRight.chunked(2).forEachIndexed { index, pair ->
                if (pair.size == 2 && index < nextRight.size) {
                    val nextMatch = nextRight[index]
                    pair.forEach { current ->
                        connectors.add(createConnector(current, nextMatch))
                    }
                }
            }
        }

        return connectors
    }

    /**
     * Create a connector from a semifinal match to the Final match.
     * The Final is in the center, so left SF connects to Final's left edge,
     * and right SF connects to Final's right edge.
     */
    private fun createConnectorToFinal(from: MatchPosition, finalMatch: MatchPosition, isFromLeftHalf: Boolean): ConnectorLine {
        val fromX = if (isFromLeftHalf) from.x + from.width else from.x
        val fromY = from.y + from.height / 2
        // Left SF connects to left edge of Final, right SF connects to right edge
        val toX = if (isFromLeftHalf) finalMatch.x else finalMatch.x + finalMatch.width
        val toY = finalMatch.y + finalMatch.height / 2

        return ConnectorLine(
            fromMatchId = from.matchId,
            toMatchId = finalMatch.matchId,
            fromX = fromX,
            fromY = fromY,
            toX = toX,
            toY = toY,
            isLeftHalf = isFromLeftHalf
        )
    }

    private fun createConnector(from: MatchPosition, to: MatchPosition): ConnectorLine {
        val fromX = if (from.isLeftHalf) from.x + from.width else from.x
        val fromY = from.y + from.height / 2
        val toX = if (from.isLeftHalf) to.x else to.x + to.width
        val toY = to.y + to.height / 2

        return ConnectorLine(
            fromMatchId = from.matchId,
            toMatchId = to.matchId,
            fromX = fromX,
            fromY = fromY,
            toX = toX,
            toY = toY,
            isLeftHalf = from.isLeftHalf
        )
    }
}
